import * as React from 'react';
import { Button, type ButtonProps } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Button3D — botão-padrão dos CTAs do app (ver CLAUDE.md #20): pill 3D sólido.
 *
 * `color` escolhe a cor (verde é o padrão do app), `size` a escala. Detalhes do
 * padrão embutidos aqui uma única vez:
 *  - o box-shadow desenha uma "base" mais escura no rodapé (`bandPx`); por isso o
 *    texto centraliza na FACE CLARA (altura − banda), não na altura total →
 *    paddingBottom = paddingTop + bandPx, empurrando o conteúdo pra cima.
 *  - hover usa brilho/escala (não classe de bg): o `background` vai inline e
 *    inline vence a classe Tailwind, então `hover:bg-*` não teria efeito.
 */

const BASE_CLASS =
  'rounded-full border-0 font-semibold transition-all hover:brightness-105 active:scale-[0.98]';

// Cor: face sólida + tons derivados (band = a mesma cor bem mais escura; glow = a
// sombra externa tingida da cor) + a classe do texto/ring.
// `text` = cor do texto via style inline (não classe Tailwind) para não depender
// da paleta gerada — ex.: `amber-950` não existe na versão de Tailwind do projeto.
const COLORS = {
  green: { face: '#2fae73', band: 'rgba(16,78,52,0.30)', glowStrong: 'rgba(30,100,70,0.28)', glowSoft: 'rgba(30,100,70,0.20)', text: '#ffffff', className: '' },
  gray: { face: '#6b7280', band: 'rgba(31,41,55,0.34)', glowStrong: 'rgba(55,65,81,0.28)', glowSoft: 'rgba(55,65,81,0.20)', text: '#ffffff', className: '' },
  // Vermelho = ações destrutivas (excluir, remover, cancelar assinatura).
  red: { face: '#dc2626', band: 'rgba(127,29,29,0.34)', glowStrong: 'rgba(153,27,27,0.30)', glowSoft: 'rgba(153,27,27,0.22)', text: '#ffffff', className: '' },
  // Verde de marca do WhatsApp (#25D366) — só para ações "enviar/abrir no WhatsApp".
  // NÃO confundir com o verde primário do app; é a cor da marca, texto branco.
  whatsapp: { face: '#25d366', band: 'rgba(11,94,60,0.34)', glowStrong: 'rgba(20,120,78,0.30)', glowSoft: 'rgba(20,120,78,0.22)', text: '#ffffff', className: '' },
  // Âmbar/dourado = PRO/premium. Face clara → texto marrom-escuro (branco não teria
  // contraste sobre o dourado).
  amber: { face: '#f59e0b', band: 'rgba(120,53,15,0.32)', glowStrong: 'rgba(180,83,9,0.30)', glowSoft: 'rgba(180,83,9,0.22)', text: '#451a03', className: '' },
  // Face branca (ações neutras/sociais, ex.: Google): texto escuro + hairline ring
  // pra destacar de fundos claros (o Card branco da tela de login).
  light: { face: '#ffffff', band: 'rgba(0,0,0,0.08)', glowStrong: 'rgba(30,41,59,0.14)', glowSoft: 'rgba(30,41,59,0.10)', text: '#374151', className: 'ring-1 ring-black/10' },
} as const;

// Escala: altura + espessura da banda + geometria da sombra externa. `padY` é o
// paddingTop; o paddingBottom deriva dele + a banda (regra de alinhamento).
const SIZES = {
  default: { className: 'h-12 px-4', bandPx: 6, padY: '0.5rem', outer: '0 10px 18px', outer2: '0 3px 6px' },
  sm: { className: 'h-9 px-3 text-sm', bandPx: 4, padY: '0.375rem', outer: '0 6px 12px', outer2: '0 2px 4px' },
} as const;

export type Button3DColor = keyof typeof COLORS;
export type Button3DSize = keyof typeof SIZES;

const buildStyle = (color: Button3DColor, size: Button3DSize): React.CSSProperties => {
  const c = COLORS[color];
  const s = SIZES[size];
  return {
    background: c.face,
    color: c.text,
    boxShadow: `inset 0 -${s.bandPx}px 0 ${c.band}, ${s.outer} ${c.glowStrong}, ${s.outer2} ${c.glowSoft}`,
    paddingTop: s.padY,
    paddingBottom: `calc(${s.padY} + ${s.bandPx}px)`,
  };
};

// Omitimos `size` do ButtonProps: aqui ele é a nossa escala 3D (não o size do
// shadcn Button), e a altura vai por classe, não pela variante do Button.
export interface Button3DProps extends Omit<ButtonProps, 'size'> {
  color?: Button3DColor;
  size?: Button3DSize;
}

/** CTA pill 3D. Encapsula estilo + regra de alinhamento; aceita as props do Button. */
export const Button3D = React.forwardRef<HTMLButtonElement, Button3DProps>(
  ({ color = 'green', size = 'default', className, style, ...props }, ref) => (
    <Button
      ref={ref}
      style={{ ...buildStyle(color, size), ...style }}
      className={cn(BASE_CLASS, SIZES[size].className, COLORS[color].className, className)}
      {...props}
    />
  ),
);
Button3D.displayName = 'Button3D';
